import React from "react";
import { View } from "react-native";
import { AppText } from "@/components/ui/AppText";
import { useI18n } from "@/context/I18nContext";
import type { ApplicationDetail } from "@/lib/api/review";
import { ReadOnlyField, FileCard } from "./Shared";
import type { MakeUpdateFn, FieldStatus } from "./Shared";
import type { ApplicationStageStatus } from "@/lib/api/vendor";
import { cn } from "@/lib/utils";

export function BrandingLocalizationContent({
  isWide,
  stageStatus,
  onMakeUpdate,
  updatedFields,
  application,
}: {
  isWide: boolean;
  stageStatus?: ApplicationStageStatus;
  onMakeUpdate?: MakeUpdateFn;
  updatedFields?: Set<string>;
  application?: ApplicationDetail;
}) {
  const { t } = useI18n();
  const data = application?.brandingLocalization;
  const fr = application?.fieldRejections;
  const fs = (key: string): FieldStatus | undefined => {
    if (updatedFields?.has(key)) return "updated";
    return fr?.[key]
      ? stageStatus === "update_required"
        ? "update_required"
        : "rejected"
      : undefined;
  };
  if (!data) return null;

  return (
    <View className={cn("items-start w-full", isWide ? "gap-0" : "px-page-x gap-4")}>
      {/* Short Name — 2-col on desktop */}
      <View className={cn("items-start w-full", isWide ? "flex-row gap-6 px-10 py-2" : "")}>
        <ReadOnlyField
          labelKey="preVetting.step5.shortNameEnglish"
          value={data.shortNameEnglish}
          flex={isWide}
          fieldStatus={fs("business_short_name_english")}
          onMakeUpdate={
            onMakeUpdate ? () => onMakeUpdate("business_short_name_english") : undefined
          }
        />
        {!isWide && <View className="border-b border-stroke-50 my-4 w-full" />}
        <ReadOnlyField
          labelKey="preVetting.step5.shortNameArabic"
          value={data.shortNameArabic ?? null}
          flex={isWide}
        />
      </View>
      <View className="border-b border-stroke-50 w-full" />

      {/* Business Description (English) */}
      <View className={cn("items-start w-full", isWide ? "px-10 py-2" : "")}>
        <ReadOnlyField
          labelKey="preVetting.step5.descriptionEnglish"
          value={data.descriptionEnglish}
          fieldStatus={fs("business_description_english")}
          onMakeUpdate={
            onMakeUpdate ? () => onMakeUpdate("business_description_english") : undefined
          }
        />
      </View>
      <View className="border-b border-stroke-50 w-full" />

      {/* Business Description (Arabic) */}
      <View className={cn("items-start w-full", isWide ? "px-10 py-2" : "")}>
        <ReadOnlyField
          labelKey="preVetting.step5.descriptionArabic"
          value={data.descriptionArabic ?? null}
        />
      </View>
      <View className="border-b border-stroke-50 w-full" />

      {/* Business Logo */}
      {data.logoUrl && (
        <View className={cn("items-start w-full", isWide ? "px-10 py-2" : "")}>
          <AppText weight="medium" className="text-base text-supporting-text">
            {t("preVetting.step5.businessLogo")}
          </AppText>
          <View className="max-w-sm items-start w-full">
            <FileCard
              url={data.logoUrl}
              fieldStatus={fs("business_logo")}
              onMakeUpdate={onMakeUpdate ? () => onMakeUpdate("business_logo") : undefined}
            />
          </View>
        </View>
      )}
    </View>
  );
}
